import React, { useEffect, useState } from "react"

import { css } from "@emotion/react"
import { type Accept, useDropzone } from "react-dropzone"

import { IFileToUploadRequestData } from "@common-shared/types/requestData/commonRequestData.types"

import { NIRVANA_COLORS } from "../config/theme.config"
import { UtilHelper } from "../helpers/util.helper"
import { PrimaryText } from "./primaryText.element"

import checkIcon from "@front-shared/assets/images/icons/check-dark.svg"
import closeIcon from "@front-shared/assets/images/icons/close-dark.svg"
import fileIcon from "@front-shared/assets/images/icons/file-dark.svg"

export const DropZoneFileElement: React.FunctionComponent<
    IDropZoneFileProps
> = props => {
    const { acceptedFiles, fileRejections, getRootProps, getInputProps } =
        useDropzone({
            maxFiles: 1,
            accept: props.acceptMimes,
        })

    const [droppedFile, setDroppedFile] = useState<IFileToUploadRequestData>()
    const [errorMsg, setErrorMsg] = useState<string>()
    const [isPredefinedProcess, setIsPredefinedProcessed] =
        useState<boolean>(false)

    useEffect(() => {
        if (!acceptedFiles.length) {
            return
        }

        void processAcceptedFile(acceptedFiles[0])
    }, [acceptedFiles])

    useEffect(() => {
        if (!fileRejections.length) {
            return
        }

        setErrorMsg(fileRejections[0].errors[0].message)
    }, [fileRejections])

    useEffect(() => {
        if (!props.predefined || isPredefinedProcess) {
            return
        }

        setIsPredefinedProcessed(true)
        setDroppedFile(props.predefined)
    }, [props.predefined])

    useEffect(() => {
        if (!errorMsg) {
            return
        }

        const timeoutId = window.setTimeout(() => {
            setErrorMsg(undefined)
        }, 5000)

        return () => {
            try {
                window.clearTimeout(timeoutId)
            } catch (e) {}
        }
    }, [errorMsg])

    const processAcceptedFile = async (file: File) => {
        let data

        try {
            data = await UtilHelper.fileToBase64(file)
        } catch (e) {
            setErrorMsg("Failed to read provided file")
        }

        if (!data) {
            return
        }

        const fileToUpload = {
            fileName: file.name,
            data,
            mime: file.type,
            extension: file.name.substr(file.name.lastIndexOf(".") + 1),
            rawFile: props.withRawFile ? file : undefined,
        } as IFileToUploadRequestData

        setDroppedFile(fileToUpload)

        props.onFileAccepted(fileToUpload)
    }

    const onCancelFile = () => {
        setDroppedFile(undefined)

        props.onFileCancel?.()
    }

    return (
        <>
            <div
                {...getRootProps({
                    className: `${props.className} relative`,
                })}
                css={[
                    styles.wrapperCommon,

                    props.inlined
                        ? styles.inlined.wrapper
                        : styles.notInlined.wrapper,

                    !droppedFile ? styles.wrapperNoDroppedFile : undefined,
                ]}
            >
                <input {...getInputProps()} />

                {!droppedFile && props.notSelectedFileContent ? (
                    props.notSelectedFileContent
                ) : (
                    <div
                        className={
                            props.inlined
                                ? "flex-1 flex items-center"
                                : "text-center"
                        }
                    >
                        <img
                            src={
                                droppedFile ? checkIcon : props.icon || fileIcon
                            }
                            alt="Upload file"
                            title="Upload file"
                            css={
                                props.inlined
                                    ? styles.inlined.uploadFileImg
                                    : styles.notInlined.uploadFileImg
                            }
                        />

                        {props.title && (!droppedFile || !props.inlined) ? (
                            <PrimaryText
                                typography={props.inlined ? "text" : "textBold"}
                                color={NIRVANA_COLORS.brand.primary}
                                css={
                                    !props.inlined
                                        ? styles.notInlined.title
                                        : undefined
                                }
                                className={props.titleClassName}
                            >
                                {props.title}
                            </PrimaryText>
                        ) : null}

                        <PrimaryText
                            typography={props.title ? "caption" : "text"}
                            css={
                                props.inlined
                                    ? styles.inlined.subTitle
                                    : styles.notInlined.subTitle
                            }
                        >
                            {droppedFile
                                ? droppedFile.fileName
                                : "Drag & drop or click to upload"}
                        </PrimaryText>
                    </div>
                )}

                {droppedFile ? (
                    <div
                        css={styles.notInlined.clearContainer}
                        onClick={e => {
                            e.stopPropagation()

                            onCancelFile()
                        }}
                    >
                        <img
                            src={closeIcon}
                            alt="Cancel file"
                            title="Cancel file"
                        />
                    </div>
                ) : null}
            </div>

            {errorMsg ? <div css={styles.error}>{errorMsg}</div> : null}
        </>
    )
}

const styles = {
    // Not inlined styles
    notInlined: {
        wrapper: css({
            padding: 24,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: 16,
        }),
        uploadFileImg: css({
            margin: "0 auto",
            width: 33,
            height: 28,
        }),
        title: css({
            marginTop: 16,
        }),
        subTitle: css({
            marginTop: 24,
        }),
        clearContainer: css({
            position: "absolute",
            zIndex: 10,
            top: 16,
            right: 16,
            cursor: "pointer",
        }),
    },
    inlined: {
        wrapper: css({
            display: "flex",
            alignItems: "center",
            paddingTop: 10,
            paddingRight: 14,
            paddingBottom: 10,
            paddingLeft: 14,
            borderRadius: 4,
        }),
        uploadFileImg: css({
            width: 14,
            marginRight: 8,
        }),
        subTitle: css({
            flex: 1,
            textAlign: "right",
            marginLeft: 16,
        }),
    },
    wrapperCommon: css({
        border: `1px solid ${NIRVANA_COLORS.brand.warmShadow}`,
    }),
    wrapperNoDroppedFile: css({
        cursor: "pointer",
    }),
    error: css({
        marginTop: 8,
        backgroundColor: NIRVANA_COLORS.brand.warmLight,
        padding: 8,
        textAlign: "center",
        borderRadius: 8,
    }),
}

interface IDropZoneFileProps {
    className?: string
    title?: string
    titleClassName?: string
    onFileAccepted: (file: IFileToUploadRequestData) => void
    onFileCancel?: () => void
    // Mime type determination is not reliable across platforms. CSV files, for example, are reported as text/plain under macOS but as application/vnd.ms-excel under Windows. In some cases there might not be a mime type set at all.
    // https://react-dropzone.js.org/#section-accepting-specific-file-types
    acceptMimes?: Accept
    predefined?: IFileToUploadRequestData
    withRawFile?: boolean
    icon?: string
    inlined?: boolean
    notSelectedFileContent?: React.JSX.Element
}
